/**
 * Unified Context Providers
 *
 * Wraps the entire application with all providers in the correct order:
 * 1. AuthProvider (Better Auth session management)
 * 2. 4-Layer Architecture (Layout, Tokens, Viewer, Persona)
 */

import { ReactNode } from 'react'
import { LayoutThemeProvider } from './LayoutThemeContext'
import { DesignTokenProvider } from './DesignTokenContext'
import { ViewerProfileProvider } from './ViewerProfileContext'
import { RolePersonaProvider } from './RolePersonaContext'
import { AuthProvider } from './AuthContext'
import type { LayoutTheme } from '@/types/layout-theme'

interface ContextProvidersProps {
  children: ReactNode
}

export function ContextProviders({ children }: ContextProvidersProps) {
  // Load initial values - check URL params first (for iframe previews), then localStorage
  const getInitialLayout = (): LayoutTheme => {
    if (typeof window === 'undefined') return 'modern-cards'
    // URL param takes priority (for iframe previews)
    const urlParams = new URLSearchParams(window.location.search)
    const urlTheme = urlParams.get('theme') as LayoutTheme | null
    if (urlTheme) return urlTheme
    // Fall back to localStorage
    return (localStorage.getItem('layoutTheme') as LayoutTheme) || 'modern-cards'
  }

  const initialLayout = getInitialLayout()
  const initialTokens = (typeof window !== 'undefined' ? localStorage.getItem('designTokenSet') as any : null) || 'elegant'
  const initialViewer = (typeof window !== 'undefined' ? localStorage.getItem('viewerProfile') as any : null) || 'public-view'
  const initialPersona = (typeof window !== 'undefined' ? localStorage.getItem('rolePersona') as any : null) || 'candidate'

  return (
    <AuthProvider>
      <LayoutThemeProvider initialTheme={initialLayout}>
        <DesignTokenProvider initialTokenSet={initialTokens}>
          <ViewerProfileProvider initialProfile={initialViewer}>
            <RolePersonaProvider initialPersona={initialPersona}>
              {children}
            </RolePersonaProvider>
          </ViewerProfileProvider>
        </DesignTokenProvider>
      </LayoutThemeProvider>
    </AuthProvider>
  )
}
